import { useState } from 'react'
import { Link } from 'react-router-dom'

import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'

import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useOrgPerm } from '@/hooks/useOrgPerm'
import { ADD_NEW_APP_ROUTE, ORG_APP_ROUTE, ORG_BILLING_ROUTE, ORG_ROUTE } from '@/shared/constants'
import { InviteMemberModal } from '@/components/InviteMemberModal'

import { ReactComponent as IconCreditCard } from '@/assets/vectors/icon_lucide_credit_card.svg?react'
import { ReactComponent as IconSmartphone } from '@/assets/vectors/icon_lucide_smartphone.svg?react'
import { ReactComponent as IconUsers } from '@/assets/vectors/icon_lucide_users.svg?react'

export default function QuickActionSection() {
  const [showInvite, setShowInvite] = useState(false)

  const canAddNewApp = useOrgPerm(OrgPermission.AppsCreateNewApp)
  const canSetBilling = useOrgPerm(OrgPermission.BillingManagePaymentMethods)
  const canInviteMember = useOrgPerm(OrgPermission.MembersInviteMembers)

  const { currentOrg } = useCurrentOrgContext()

  if (!currentOrg) return null

  return (
    <>
      <div className='bg-white dark:bg-slate-800/50 rounded-xl p-6 backdrop-blur-xs border border-slate-200/50 dark:border-slate-700/50 shadow-xs mb-8'>
        <h2 className='text-lg font-medium text-slate-900 dark:text-white mb-6'>Quick Actions</h2>
        <div className='grid grid-cols-3 gap-4'>
          {canAddNewApp && (
            <Link
              to={`${ORG_ROUTE}/${currentOrg.id}/${ORG_APP_ROUTE}/${ADD_NEW_APP_ROUTE}`}
              className='group relative p-4 bg-slate-100 dark:bg-slate-700/30 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700/50 transition-all'
            >
              <div className='flex flex-col items-center gap-2 text-center'>
                <span className='inline-flex p-2 rounded-lg bg-teal-50 dark:bg-teal-500/10 text-teal-600 dark:text-teal-400 group-hover:bg-teal-100 dark:group-hover:bg-teal-500/20 transition-colors'>
                  <IconSmartphone className='h-6 w-6 fill-transparent' />
                </span>
                <span className='font-medium text-slate-900 dark:text-white'>Build New App</span>
                <span className='text-sm text-slate-600 dark:text-slate-400'>Create a new application</span>
              </div>
            </Link>
          )}

          {canInviteMember && (
            <button
              onClick={() => setShowInvite(true)}
              className='group relative p-4 bg-slate-100 dark:bg-slate-700/30 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700/50 transition-all'
            >
              <div className='flex flex-col items-center gap-2 text-center'>
                <span className='inline-flex p-2 rounded-lg bg-teal-50 dark:bg-teal-500/10 text-teal-600 dark:text-teal-400 group-hover:bg-teal-100 dark:group-hover:bg-teal-500/20 transition-colors'>
                  <IconUsers className='h-6 w-6 fill-transparent' />
                </span>
                <span className='font-medium text-slate-900 dark:text-white'>Invite Team</span>
                <span className='text-sm text-slate-600 dark:text-slate-400'>Add new members</span>
              </div>
            </button>
          )}

          {canSetBilling && (
            <Link
              to={`${ORG_ROUTE}/${currentOrg.id}/${ORG_BILLING_ROUTE}`}
              className='group relative p-4 bg-slate-100 dark:bg-slate-700/30 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700/50 transition-all'
            >
              <div className='flex flex-col items-center gap-2 text-center'>
                <span className='inline-flex p-2 rounded-lg bg-teal-50 dark:bg-teal-500/10 text-teal-600 dark:text-teal-400 group-hover:bg-teal-100 dark:group-hover:bg-teal-500/20 transition-colors'>
                  <IconCreditCard className='h-6 w-6 fill-transparent' />
                </span>
                <span className='font-medium text-slate-900 dark:text-white'>Billing</span>
                <span className='text-sm text-slate-600 dark:text-slate-400'>Manage payments</span>
              </div>
            </Link>
          )}
        </div>
      </div>

      <InviteMemberModal
        show={showInvite}
        withButton={false}
        setShow={setShowInvite}
        org={currentOrg}
        onSuccess={(closeModal) => {
          closeModal()
        }}
      />
    </>
  )
}
